import { AerInputStructure } from "@aerstudios/aerated-aerinputstructure";
import { AerRadioGroup } from "@aerstudios/aerated-aerradiogroup";
import cx from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import * as styles from "./RadioGroup.module.scss";

export interface RadioGroupProps {
	// The id of the Radio group
	id: string;
	// The label for the Radio group
	groupLabel: string | { text: string; hide: boolean };
	// The options for the Radio group
	options: NDS.FormOption[];
	// The value of the selected radio button
	value: string;
	// The function to call when a Radio is selected
	onChange: (value?: string) => void;
	// The error message to display
	errorMessage?: string;
	// Whether the element is hidden
	hidden?: boolean;
	isYesNoVariant?: boolean;
	// Whether the group needs a linebreak
	hasLinebreak?: boolean;
	// Whether the group is a group using images
	isImageGroup?: boolean;
	disabled?: boolean;
}
export const NDSRadioGroup: React.FC<RadioGroupProps> = ({
	id,
	groupLabel,
	options,
	value,
	onChange,
	errorMessage,
	hidden,
	isYesNoVariant = false,
	hasLinebreak = false,
	isImageGroup,
	disabled,
}) => {
	// Radio Group
	// Is single-select
	// Has a label
	// Label can have an additional, non bolded text
	const [key, setKey] = useState(0);

	// This is a hack to force the radio group to re-render first time and load the incoming value.
	useEffect(() => {
		setKey((prev) => prev + 1);
	}, []);

	const radioOptions = useMemo(
		() =>
			options?.map((answer, i) => {
				const isChecked = value === answer.value;
				const hasImage = answer.imgSrc;
				const labelContent = hasImage ? (
					<>
						<img
							id={`${answer.text}-image`}
							className={styles.radioImage}
							src={answer.imgSrc}
							alt={""}
							aria-hidden="true"
						/>
						<span
							className={styles.radioGroupOptionWithImageLabel}
							// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
							dangerouslySetInnerHTML={{ __html: answer.label }}
						/>
					</>
				) : (
					<span
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
						dangerouslySetInnerHTML={{ __html: answer.label }}
						className={styles.labelContent}
					/>
				);
				return (
					<AerRadioGroup.Item
						key={`${answer.label.slice(0, 5)}-${i}`}
						id={`${answer.value}-input`}
						label={labelContent}
						value={answer.value}
						checkedIcon={<div className={styles.selectedRadio} />}
						checked={isChecked}
						className={cx(
							!isYesNoVariant ? styles.radioOption : styles.radioOptionYesNo,
							{
								[styles.firstItem]: i === 0,
								[styles.radioOptionWithImage]: hasImage,
								[styles.radioOptionWithImageSelected]: isChecked && hasImage,
							},
						)}
					/>
				);
			}),
		[isYesNoVariant, options, value],
	);

	return (
		<AerInputStructure.Root
			className={
				!isYesNoVariant
					? styles.radioGroupWrapper
					: styles.radioGroupWrapperYesNo
			}
			id={id}
			errorMessage={errorMessage}
		>
			<AerRadioGroup.Root
				key={key}
				groupLabel={groupLabel}
				groupLabelClassName={styles.radioGroupLabel}
				value={value}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
					}
				}}
				className={cx(
					!isYesNoVariant ? styles.radioGroup : styles.radioGroupYesNo,
					{
						[styles.radioGroupWithImages]: isImageGroup,
					},
				)}
				onChange={(val) =>
					onChange((val as string) === value ? undefined : (val as string))
				}
				hidden={hidden}
				defaultValue={value}
				defaultChecked={true}
				disabled={disabled}
			>
				{radioOptions}
			</AerRadioGroup.Root>
			<AerInputStructure.ErrorMessage />
			{hasLinebreak && <hr className={styles.linebreak} />}
		</AerInputStructure.Root>
	);
};

export default NDSRadioGroup;
